import * as bitcoin from 'bitcoinjs-lib'
import Wallet, { ensureEccLib } from '@/utils/wallet'
import { SecurePurchaseUnavailableError } from './secure-purchase'
import { suppressAccountReload } from '@/utils/wallet/account-change-guard'
import { isIosTextEntry } from '@/utils/ios-text-entry'
import { buyerInputIndexes, classifyPsbtInput, inputSighashTypes, parsePsbt } from './secure-psbt'

type SecureSignPlan = {
	psbt: string
	indexes: number[]
	/**
	 * The input is a Taproot script-path spend of the wallet's own key (the
	 * passthrough v4 sale template). Wallets that tweak by default must be told
	 * to sign with the untweaked key, and the input must come back unfinalized:
	 * Ordinals Wallet adds the second signature at purchase time.
	 */
	tapscript?: boolean
}

// ---- shared -----------------------------------------------------------------

function requirePublicKey(): string {
	const publicKey = Wallet.getPublicKey()
	if (!publicKey) {
		throw new SecurePurchaseUnavailableError(
			'wallet_public_key_unavailable',
			'Connect the wallet again to sign'
		)
	}
	return publicKey
}

/**
 * UniSat-style `toSignInputs`. Identify the signer by public key only: these
 * wallets compare a supplied `address` with the active account's address and
 * throw "invalid address in toSignInput" when it differs, and the sale
 * template's input is the escrow output, not any account address.
 * `sighashTypes` must be forwarded or only SIGHASH_ALL is allowed. A Taproot
 * input with no sighash in the PSBT is asked for as SIGHASH_DEFAULT: UniSat
 * signs key-path Taproot with DEFAULT and rejects a hint that says ALL.
 * (Xverse reads the sighash from the PSBT itself; its hint stays ALL.)
 */
function unisatStyleInputs(plan: SecureSignPlan) {
	const publicKey = requirePublicKey()
	const sighashTypes = inputSighashTypes(plan.psbt, plan.indexes, { taprootDefault: true })
	return plan.indexes.map((index, i) => ({
		index,
		publicKey,
		sighashTypes: sighashTypes[i],
		...(plan.tapscript ? { disableTweakSigner: true } : {})
	}))
}

function unisatStyleOptions(plan: SecureSignPlan) {
	return { autoFinalized: !plan.tapscript, toSignInputs: unisatStyleInputs(plan) }
}

function expectSigned(name: string, data: unknown, count: number): string[] {
	if (!Array.isArray(data) || data.length !== count || !data.every((v) => typeof v === 'string')) {
		throw new SecurePurchaseUnavailableError(
			'sign_failed',
			`${name} returned an incomplete signature set`
		)
	}
	return data as string[]
}

function toOutputScript(address: string): Buffer | null {
	try {
		return bitcoin.address.toOutputScript(address, bitcoin.networks.bitcoin)
	} catch {
		return null
	}
}

/**
 * Which of the wallet's addresses an input belongs to. The sale template's
 * input is the escrow, signed by the ordinals (Taproot) key; funding and
 * passthrough inputs are matched by script against the wallet's addresses.
 */
function addressForInput(psbt: bitcoin.Psbt, index: number, tapscript: boolean): string {
	const taproot = Wallet.getTaprootAddress()
	const payment = Wallet.balanceAddress()
	if (tapscript) return taproot
	const script = psbt.data.inputs[index]?.witnessUtxo?.script
	if (script) {
		for (const candidate of [taproot, payment]) {
			const expected = candidate ? toOutputScript(candidate) : null
			if (expected && Buffer.from(script).equals(expected)) return candidate
		}
	}
	return taproot || payment
}

function psbtHexFromBase64(base64: string): string {
	return Buffer.from(base64, 'base64').toString('hex')
}

/**
 * UniSat-family wallets sign with whatever account is connected to this site
 * and reject a `publicKey` that is not that account's ("invalid public key in
 * toSignInput"). Two ways to get there: the extension is locked or dropped
 * the site connection (no account at all), or it is on another account or
 * address type. Reconnect for the first, explain the second.
 */
async function assertLiveAccount(provider: any, name: string, signal?: AbortSignal): Promise<void> {
	const expected = (Wallet.getPublicKey() || '').toLowerCase()
	const read = async () => {
		const [live, accounts] = await Promise.all([
			provider.getPublicKey?.(),
			provider.getAccounts?.()
		])
		return {
			live: typeof live === 'string' ? live.toLowerCase() : '',
			accounts: Array.isArray(accounts) ? (accounts as string[]) : []
		}
	}
	const withTimeout = <T>(p: Promise<T>, ms: number, label: string): Promise<T> =>
		new Promise<T>((resolve, reject) => {
			const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms} ms`)), ms)
			p.then(
				(v) => {
					clearTimeout(timer)
					resolve(v)
				},
				(e) => {
					clearTimeout(timer)
					reject(e)
				}
			)
		})
	let state: { live: string; accounts: string[] }
	try {
		console.info('[secure-sign] account probe', { wallet: name })
		state = await withTimeout(read(), 8_000, `${name} account probe`)
		signal?.throwIfAborted()
		if (!state.live || state.accounts.length === 0) {
			// Not connected to this origin (or locked): ask, which also unlocks.
			console.info('[secure-sign] requesting accounts', { wallet: name })
			// Connecting fires `accountsChanged`; the adapters would reload the
			// page mid-flow. Hold the reload while we connect and re-probe.
			suppressAccountReload(130_000)
			try {
				await withTimeout(Promise.resolve(provider.requestAccounts?.()), 120_000, `${name} connect`)
				signal?.throwIfAborted()
				state = await withTimeout(read(), 8_000, `${name} account probe`)
			} finally {
				suppressAccountReload(0)
			}
		}
	} catch (e: any) {
		signal?.throwIfAborted()
		// Which step failed decides what the user (and #errors) should see:
		// a probe that never answered is the extension hanging or a clone of
		// `window.unisat` from another wallet; a refused connect is the user
		// or a locked extension. Each gets its own code so the wallet-health
		// panel counts them apart.
		const reason = String(e?.message || 'request rejected')
		const code = /timed out/.test(reason) ? 'wallet_probe_timeout' : 'wallet_connect_rejected'
		const fingerprint = providerFingerprint(name, provider, window)
		// Two Bitcoin extensions in one browser fight over the page; the one
		// that wins may not answer for the other. Say which and what to do.
		const rival = conflictingExtension(name, window)
		throw new SecurePurchaseUnavailableError(
			rival && code === 'wallet_probe_timeout' ? 'wallet_extension_conflict' : code,
			rival
				? `${name} did not answer: ${reason}; injected=${fingerprint}. ${rival} is also installed and can take over ${name}'s connection. Disable ${rival} for this site (or turn off its option to replace other wallets), reload, then try again.`
				: `${name} did not connect this site: ${reason}; injected=${fingerprint}. Unlock ${name}, approve the connection, then try again.`
		)
	}
	// Diagnostic for support: all of this is public key material.
	console.info('[secure-sign] account check', {
		wallet: Wallet.type(),
		stored_public_key: expected,
		stored_address: Wallet.getTaprootAddress(),
		live_public_key: state.live,
		live_accounts: state.accounts
	})
	if (!state.live || state.accounts.length === 0) {
		throw new SecurePurchaseUnavailableError(
			'wallet_not_connected',
			`${name} connected but returned no account (live_key=${state.live ? 'yes' : 'no'} accounts=${
				state.accounts.length
			}; injected=${providerFingerprint(
				name,
				provider,
				window
			)}). Unlock ${name}, connect it to this site, then try again.`
		)
	}
	if (state.live === expected) return
	throw new SecurePurchaseUnavailableError(
		'wallet_account_mismatch',
		`${name} is on a different account or address type (${
			state.accounts[0]
		}) than the one connected to Ordinals Wallet (${Wallet.getTaprootAddress()}). Switch ${name} back to that Taproot address, or disconnect and reconnect, then try again.`
	)
}

// ---- UniSat / OYL / OKX / TAP -----------------------------------------------------

async function signUnisat(
	psbts: string[],
	plans: SecureSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	await assertLiveAccount((window as any).unisat, 'UniSat', signal)
	signal?.throwIfAborted()
	const options = plans.map(unisatStyleOptions)
	console.info('[secure-sign] UniSat signPsbts', {
		psbts: psbts.length,
		inputs: options.map((o) => o.toSignInputs.length)
	})
	const data = await (window as any).unisat.signPsbts(psbts, options)
	return expectSigned('UniSat', data, psbts.length)
}

async function signOyl(
	psbts: string[],
	plans: SecureSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	await assertLiveAccount((window as any).oyl, 'OYL', signal)
	signal?.throwIfAborted()
	const data = await (window as any).oyl.signPsbts(psbts, plans.map(unisatStyleOptions))
	return expectSigned('OYL', data, psbts.length)
}

async function signOkx(
	psbts: string[],
	plans: SecureSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	const okx = (window as any).okxwallet.bitcoin
	await assertLiveAccount(okx, 'OKX', signal)
	signal?.throwIfAborted()
	const data = await okx.signPsbts(psbts, plans.map(unisatStyleOptions))
	const signed = expectSigned('OKX', data, psbts.length)
	// OKX's batch signer has ignored `disableTweakSigner` and signed the sale
	// template on the key path (sale_signed_on_key_path, 2026-09-24: 8 of 11
	// OKX listings). Its single-PSBT signer honours the option, so re-sign
	// just those templates one at a time before giving up on protection.
	for (let i = 0; i < plans.length; i++) {
		const plan = plans[i]
		if (!plan.tapscript || !signedOnKeyPathOnly(signed[i], plan.indexes)) continue
		signal?.throwIfAborted()
		try {
			const again = await okx.signPsbt(psbts[i], unisatStyleOptions(plan))
			if (typeof again === 'string' && again.trim()) signed[i] = again
		} catch {
			// Keep the batch result; the template check reports the real code.
		}
	}
	return signed
}

/** True when every tapscript input carries only a key-path signature. */
function signedOnKeyPathOnly(psbtHex: string, indexes: number[]): boolean {
	try {
		const psbt = parsePsbt(psbtHex)
		return indexes.every((index) => {
			const input = psbt.data.inputs[index]
			const scriptSigned = Boolean(input?.tapScriptSig && input.tapScriptSig.length)
			const finalizedScript = Boolean(
				input?.finalScriptWitness && input.finalScriptWitness.length > 66
			)
			return !scriptSigned && !finalizedScript && Boolean(input?.tapKeySig?.length)
		})
	} catch {
		return false
	}
}

async function signTap(
	psbts: string[],
	plans: SecureSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	await assertLiveAccount((window as any).tapprotocol, 'TAP', signal)
	signal?.throwIfAborted()
	// TAP mirrors UniSat's options under `inputForSigns`.
	const options = plans.map((plan) => ({
		autoFinalized: !plan.tapscript,
		inputForSigns: unisatStyleInputs(plan)
	}))
	const data = await (window as any).tapprotocol.signPsbts(psbts, options)
	return expectSigned('TAP', data, psbts.length)
}

// ---- Xverse -------------------------------------------------------------------

async function signXverse(psbts: string[], plans: SecureSignPlan[]): Promise<string[]> {
	const { signMultipleTransactions, signTransaction } = await import('sats-connect')
	const entries = plans.map((plan) => {
		const psbt = parsePsbt(plan.psbt)
		const sighashes = inputSighashTypes(plan.psbt, plan.indexes)
		const inputsToSign = plan.indexes.map((index, i) => {
			const sigHash = sighashes[i]?.[0]
			return {
				address: addressForInput(psbt, index, Boolean(plan.tapscript)),
				signingIndexes: [index],
				...(sigHash !== undefined ? { sigHash } : {})
			}
		})
		return { psbtBase64: Buffer.from(plan.psbt, 'hex').toString('base64'), inputsToSign }
	})
	// Xverse mobile's combined review for the protected parent/child pair can
	// leave Confirm disabled; its last detail page only has a disabled Next.
	// Give each PSBT its own approval instead. Keep the sale unfinalized and
	// never broadcast either template: authorize-bulk still needs BOTH signatures.
	// Desktop and purchase requests keep their existing batched signing flow.
	const mobile =
		isIosTextEntry() || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent))
	if (mobile && plans.some((plan) => plan.tapscript)) {
		const signed: string[] = []
		for (const [index, entry] of entries.entries()) {
			const psbt = await new Promise<string>((resolve, reject) => {
				signTransaction({
					payload: {
						network: { type: 'Mainnet' as any },
						message: `Sign protected listing ${index + 1} of ${entries.length}`,
						...entry,
						broadcast: false
					},
					onFinish: (response) => {
						try {
							const hex = psbtHexFromBase64(response.psbtBase64)
							parsePsbt(hex)
							resolve(hex)
						} catch (e) {
							reject(e)
						}
					},
					onCancel: () =>
						reject(new SecurePurchaseUnavailableError('sign_cancelled', 'Signing was cancelled'))
				}).catch(reject)
			})
			signed.push(psbt)
		}
		return signed
	}
	return await new Promise<string[]>((resolve, reject) => {
		signMultipleTransactions({
			payload: {
				network: { type: 'Mainnet' as any },
				message: 'Sign the protected listing',
				psbts: entries
			},
			onFinish: (responses: any) => {
				try {
					const list = Array.isArray(responses) ? responses : [responses]
					// Nothing is finalized here: the sale template must stay half-signed
					// and the API completes every other input itself.
					resolve(
						expectSigned(
							'Xverse',
							list.map((r: any) => psbtHexFromBase64(r.psbtBase64)),
							psbts.length
						)
					)
				} catch (e) {
					reject(e)
				}
			},
			onCancel: () =>
				reject(new SecurePurchaseUnavailableError('sign_cancelled', 'Signing was cancelled'))
		} as any).catch(reject)
	})
}

// ---- Phantom ------------------------------------------------------------------

async function signPhantom(psbts: string[], plans: SecureSignPlan[]): Promise<string[]> {
	const out: string[] = []
	for (const plan of plans) {
		const psbt = parsePsbt(plan.psbt)
		const sighashes = inputSighashTypes(plan.psbt, plan.indexes)
		const inputsToSign = plan.indexes.map((index, i) => ({
			address: addressForInput(psbt, index, Boolean(plan.tapscript)),
			signingIndexes: [index],
			sigHash: sighashes[i]?.[0] ?? 1
		}))
		const signed = await (window as any).phantom.bitcoin.signPSBT(Buffer.from(plan.psbt, 'hex'), {
			inputsToSign
		})
		out.push(Buffer.from(signed).toString('hex'))
	}
	return out
}

// ---- Leather ------------------------------------------------------------------

async function signLeather(psbts: string[], plans: SecureSignPlan[]): Promise<string[]> {
	const provider = (window as any).LeatherProvider || (window as any).btc
	if (!provider?.request) {
		throw new SecurePurchaseUnavailableError(
			'unsupported_wallet',
			'Leather provider is not available'
		)
	}
	const out: string[] = []
	for (const plan of plans) {
		const sighashes = inputSighashTypes(plan.psbt, plan.indexes)
		const allowedSighash = Array.from(new Set(sighashes.flat().length ? sighashes.flat() : [0, 1]))
		const response = await provider.request('signPsbt', {
			hex: plan.psbt,
			signAtIndex: plan.indexes,
			allowedSighash,
			network: 'mainnet',
			broadcast: false
		})
		const hex = response?.result?.hex
		if (typeof hex !== 'string') {
			throw new SecurePurchaseUnavailableError('sign_failed', 'Leather returned no signed PSBT')
		}
		out.push(hex)
	}
	return out
}

// ---- dispatch -------------------------------------------------------------------

/**
 * Sign only the caller's inputs, per plan. Every external wallet gets an
 * explicit index list: default adapters detect owned inputs by matching the
 * prevout script against the wallet's addresses, which never matches the
 * sale template's escrow input, and most of them finalize what they sign.
 * The native signer already handles both cases itself.
 */
export async function signOwnedPsbts(
	plans: SecureSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	signal?.throwIfAborted()
	await ensureEccLib()
	signal?.throwIfAborted()
	if (plans.length === 0) return []
	const psbts = plans.map((plan) => plan.psbt)
	const type = Wallet.type()
	const w = window as any
	console.info('[secure-sign] signing', {
		wallet: type,
		plans: plans.map((p) => ({ inputs: p.indexes.length, tapscript: Boolean(p.tapscript) })),
		stored_public_key: Wallet.getPublicKey(),
		stored_address: Wallet.getTaprootAddress()
	})

	const missing = missingExtensionProvider(type, w)
	if (missing) {
		console.info('[secure-sign] provider missing', {
			wallet: type,
			providers: injectedProviders(w)
		})
		throw new SecurePurchaseUnavailableError(
			'provider_missing',
			`${missing} is not injected in this tab (found: ${
				injectedProviders(w).join(',') || 'none'
			}). Unlock the ${missing} extension, reload the page and connect it again, then retry.`
		)
	}

	if (type === 'unisat') return signUnisat(psbts, plans, signal)
	if (type === 'oyl') return signOyl(psbts, plans, signal)
	if (type === 'okx') return signOkx(psbts, plans, signal)
	if (type === 'tap') return signTap(psbts, plans, signal)
	if (type === 'xverse') return signXverse(psbts, plans)
	if (type === 'phantom') return signPhantom(psbts, plans)
	if (type === 'hiro') return signLeather(psbts, plans)

	const { psbts: signed, success } = await Wallet.signPSBTS(psbts)
	if (!success || !signed || signed.length !== psbts.length) {
		throw new SecurePurchaseUnavailableError('sign_failed', 'Wallet did not return signed PSBTs')
	}
	return signed
}

export function listingSignPlan(psbt: string): SecureSignPlan {
	const parsed = parsePsbt(psbt)
	classifyPsbtInput(parsed, 0)
	// One or two inputs: the item, and optionally a seller UTXO paying the fee.
	return { psbt, indexes: Array.from({ length: parsed.inputCount }, (_, i) => i) }
}

/** The v4 sale template: one script-path input of the seller's own key. */
export function saleTemplateSignPlan(psbt: string): SecureSignPlan {
	return { psbt, indexes: [0], tapscript: true }
}

/**
 * Sighash types a buyer input may ever be asked for. Anything with
 * ANYONECANPAY (0x81, 0x83) produces a signature that stays valid in other
 * transactions, so a malicious template could reuse it; the buyer's inputs
 * are always plain SIGHASH_ALL (or DEFAULT for taproot).
 */
const BUYER_SIGHASHES = new Set([0x00, 0x01])

export function purchaseSignPlans(psbts: string[], buyerScripts: Buffer[]): SecureSignPlan[] {
	return psbts.map((psbt) => {
		const indexes = buyerInputIndexes(psbt, buyerScripts)
		if (indexes.length === 0) {
			throw new SecurePurchaseUnavailableError(
				'missing_buyer_input',
				'Purchase PSBT has no input owned by this wallet'
			)
		}
		for (const [position, sighash] of inputSighashTypes(psbt, indexes).entries()) {
			const requested = sighash[0]
			if (requested !== undefined && !BUYER_SIGHASHES.has(requested)) {
				throw new SecurePurchaseUnavailableError(
					'buyer_sighash',
					`Wallet was asked to sign input ${indexes[position]} with sighash 0x${requested
						.toString(16)
						.padStart(2, '0')}; a purchase only ever uses SIGHASH_ALL`
				)
			}
		}
		return { psbt, indexes }
	})
}

/**
 * Extension wallets sign through an injected provider. When the stored wallet
 * type says "OKX" but the page has no `okxwallet.bitcoin` (extension locked,
 * removed, or a different browser profile), every signing path below would
 * dereference `undefined`; report the missing provider by name instead.
 * Returns the wallet's display name, or null when the provider is present or
 * the wallet type does not use an injected provider.
 */
export function missingExtensionProvider(type: string | null | undefined, w: any): string | null {
	switch (type) {
		case 'unisat':
			return typeof w?.unisat?.signPsbts === 'function' ? null : 'UniSat'
		case 'oyl':
			return typeof w?.oyl?.signPsbts === 'function' ? null : 'OYL'
		case 'okx':
			return typeof w?.okxwallet?.bitcoin?.signPsbts === 'function' ? null : 'OKX Wallet'
		case 'tap':
			return typeof w?.tapprotocol?.signPsbts === 'function' ? null : 'TAP Wallet'
		case 'phantom':
			return typeof w?.phantom?.bitcoin?.signPSBT === 'function' ? null : 'Phantom'
		case 'hiro':
			return w?.LeatherProvider || w?.btc ? null : 'Leather'
		default:
			return null
	}
}

/**
 * What is actually behind the provider object we are about to sign with.
 * Several wallets (Wizz, Bitget, OKX, Binance) also inject `window.unisat`,
 * and the last one to load wins; a UniSat user whose "UniSat" is really
 * another extension probes as not connected. Public, non-identifying flags.
 */
export function providerFingerprint(name: string, provider: any, w: any): string {
	const flags: string[] = []
	try {
		for (const k of Object.keys(provider || {})) {
			if (/^(is|_is)[A-Z]/.test(k) && provider[k]) flags.push(k)
		}
		if (provider?._isUnisat === false || provider?.isWizz || provider?.isBitKeep)
			flags.push('clone')
		// OKX can answer for other wallets; an alias of its own provider is
		// the smoking gun. The constructor name is a weaker hint.
		if (provider && (provider === w?.okxwallet?.bitcoin || provider === w?.okxwallet))
			flags.push('okx-alias')
		const ctor = Object.getPrototypeOf(provider)?.constructor?.name
		if (ctor && ctor !== 'Object') flags.push(`ctor:${String(ctor).slice(0, 24)}`)
	} catch {
		/* provider proxies may throw on enumeration */
	}
	const all = injectedProviders(w).join(',') || 'none'
	return `${name}[${flags.join(',') || 'no-flags'}] of ${all}`.slice(0, 120)
}

/** A second Bitcoin extension known to take over other wallets' connections. */
export function conflictingExtension(name: string, w: any): string | null {
	if (name !== 'OKX' && w?.okxwallet?.bitcoin) return 'OKX Wallet'
	return null
}

/** Names of the Bitcoin providers injected into the page, for diagnostics only. */
export function injectedProviders(w: any): string[] {
	const found: string[] = []
	if (w?.unisat) found.push('unisat')
	if (w?.oyl) found.push('oyl')
	if (w?.okxwallet?.bitcoin) found.push('okxwallet.bitcoin')
	if (w?.okxwallet && !w?.okxwallet?.bitcoin) found.push('okxwallet (no .bitcoin)')
	if (w?.tapprotocol) found.push('tapprotocol')
	if (w?.phantom?.bitcoin) found.push('phantom.bitcoin')
	if (w?.LeatherProvider || w?.btc) found.push('leather')
	if (w?.XverseProviders || w?.BitcoinProvider) found.push('xverse')
	return found
}
